/**
 * DashboardHome — redesigned /dashboard landing page (CP-8071).
 *
 * Replaces the plain "Welcome! / Dashboard How-To Guides" link list with:
 *   • a page header consistent with Analytics / Users
 *   • a welcome banner
 *   • ScribeHow guides as icon + description cards, grouped into three sections
 *   • a dedicated support + emergency escalation panel
 *
 * Only @mui/material + @mui/icons-material are used — no new dependencies.
 * GUIDE_SECTIONS is data-driven: add a guide by adding an object, no JSX edits.
 */

import * as React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LuggageIcon from '@mui/icons-material/Luggage';
import PublicIcon from '@mui/icons-material/Public';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import MenuBookIcon from '@mui/icons-material/MenuBook';

import PageMeta from '../components/PageMeta';
import { FinishSetupCard } from '../onboarding';
import { SUPPORT_EMAIL } from '../utils/support';
import { ContextStore } from '../store/contextStore';

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

const GUIDE_SECTIONS = [
  {
    title: 'Getting Started & Admin',
    subtitle: 'Accounts, access, notifications and the everyday questions.',
    guides: [
      {
        title: 'Dashboard FAQs',
        icon: HelpOutlineIcon,
        href: 'https://scribehow.com/page/Dashboard_FAQs__CBHUSoe0Sw69rHj3RSxMOQ',
        description:
          'Add users and guides, manage access groups, reset passwords, export analytics, enable desktop notifications and restore archived chats.',
        chips: [{ label: '21 guides' }, { label: 'Scribe collection', tone: 'scribe' }],
      },
      {
        title: 'Setting Up AI Suite',
        icon: AutoAwesomeIcon,
        href: 'https://scribehow.com/page/Setting_Up_AI_Suite__PYHv0fIxRRKe_N7gyOphMQ',
        description:
          'Turn on suggested responses and auto-reply, and let auto-tagging categorise incoming guest messages for you.',
        chips: [{ label: '2 guides' }, { label: 'Scribe collection', tone: 'scribe' }],
      },
    ],
  },
  {
    title: 'Building Your Content',
    subtitle: 'Set up trips, catalog listings and activities, tab by tab.',
    guides: [
      {
        title: 'My Trips',
        icon: LuggageIcon,
        href: 'https://scribehow.com/page/MyTrips_Guides__AgNU7XbTQl-AC5SeQvWq8g',
        description:
          'Trip names and descriptions, day-by-day itinerary titles and timelines, linking activities to tours, transportation and trip items.',
        chips: [{ label: '10 guides' }, { label: 'Trip Info · Itinerary · Trip Items' }],
      },
      {
        title: 'Catalog',
        icon: PublicIcon,
        href: 'https://scribehow.com/page/Catalog_Guides__MLQa8I_USLurfL79RDQ6Lg',
        description:
          'Tour titles, synopsis, duration, pricing and currency, regions and filters, catalog images and itinerary highlights.',
        chips: [{ label: '13 guides' }, { label: 'General Info · Images · Highlights' }],
      },
      {
        title: 'Activities',
        icon: LocalActivityIcon,
        href: 'https://scribehow.com/page/Activity_Guides__FkzBka-oTb6SfePTZvxTTQ',
        description:
          'Create activities from Google Places or custom, write display names and descriptions, set pricing, and attach images, audio, documents and nearby recommendations.',
        chips: [{ label: '12 guides' }, { label: '4 tabs covered' }],
      },
    ],
  },
  {
    title: 'Day-of Operations',
    subtitle: 'What your guides and ops team need on departure day.',
    guides: [
      {
        title: 'Pre-Check-In',
        icon: AssignmentTurnedInIcon,
        href: 'https://scribehow.com/viewer/_Pre-Check-In__KqY1K6qjQemEn-gMFGJL4A',
        description:
          'Collect guest details ahead of departure so nothing is chased on the day of the tour.',
        chips: [{ label: 'Single walkthrough', tone: 'walkthrough' }],
      },
      {
        title: 'Guide Check-In',
        icon: HowToRegIcon,
        href: 'https://scribehow.com/viewer/How_to_use_Check-in_Feature__MYlDxP-ZT6icSYD4UNyMZg',
        description:
          'How guides use the check-in feature to mark guests on board and track headcount in real time.',
        chips: [{ label: 'Single walkthrough', tone: 'walkthrough' }],
      },
    ],
  },
];

const TOTAL_GUIDES = GUIDE_SECTIONS.flatMap((s) => s.guides).reduce((sum, g) => {
  const match = /^(\d+) guides$/.exec(g.chips[0].label);
  return sum + (match ? Number(match[1]) : 1);
}, 0);

const STATUS_URL = 'http://status.touroptima.com/';
const EMERGENCY_PHONE = '+1 (646) 397-3459';

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

const chipSx = (tone) => {
  if (tone === 'scribe') {
    return { bgcolor: '#fdf1f6', color: '#b0417a', borderColor: '#f6d7e5' };
  }
  if (tone === 'walkthrough') {
    return { bgcolor: '#e9f7ef', color: '#1e7a48', borderColor: '#cdebd9' };
  }
  return { bgcolor: 'grey.100', color: 'text.secondary', borderColor: 'divider' };
};

function GuideCard({ guide }) {
  const Icon = guide.icon;
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        height: '100%',
        transition: (t) =>
          t.transitions.create(['box-shadow', 'border-color', 'transform'], { duration: 160 }),
        '&:hover': {
          borderColor: 'primary.light',
          boxShadow: 3,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardActionArea
        component="a"
        href={guide.href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ height: '100%', alignItems: 'flex-start', p: 2 }}
      >
        <Stack direction="row" spacing={1.75} alignItems="flex-start">
          <Box
            sx={{
              flex: '0 0 auto',
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.dark',
              // ~16% tint of the theme primary, so it tracks any brand colour change
              backgroundColor: (t) => alpha(t.palette.primary.main, 0.12),
            }}
          >
            <Icon fontSize="small" />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                {guide.title}
              </Typography>
              <OpenInNewIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
              {guide.description}
            </Typography>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {guide.chips.map((chip) => (
                <Chip
                  key={chip.label}
                  label={chip.label}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: 11, fontWeight: 500, ...chipSx(chip.tone) }}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <Box sx={{ mb: 1.75 }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, letterSpacing: '.09em', color: 'text.secondary' }}
        >
          {title}
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Stack>
      {subtitle && (
        <Typography variant="body2" color="text.disabled">
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function SupportRow({ icon: Icon, label, children, tone }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1,
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: tone === 'warning' ? 'rgba(255,255,255,.7)' : 'grey.100',
          color: tone === 'warning' ? 'warning.dark' : 'text.secondary',
        }}
      >
        <Icon sx={{ fontSize: 15 }} />
      </Box>
      <Box>
        <Typography variant="caption" color={tone === 'warning' ? 'warning.dark' : 'text.disabled'}>
          {label}
        </Typography>
        <Typography variant="body2" component="div">
          {children}
        </Typography>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function DashboardHome() {
  const { user } = React.useContext(ContextStore);
  const userName = user?.first_name || user?.display_name;

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100%' }}>
      <PageMeta title="Dashboard" />

      {/* Page header — mirrors Analytics / Users */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: 3,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Dashboard
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            icon={<MenuBookIcon />}
            label={`${TOTAL_GUIDES}+ how-to guides`}
            size="small"
            variant="outlined"
          />
        </Stack>
      </Box>

      <Box sx={{ maxWidth: 1180, mx: 'auto', px: 3, pt: 3, pb: 7 }}>
        {/* What's next after easy onboarding, until every step is done or hidden (renders nothing otherwise) */}
        <FinishSetupCard />

        {/* Welcome banner */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            p: 3,
            mb: 3.25,
            color: 'common.white',
            background: (t) =>
              `linear-gradient(90deg, ${t.palette.primary.main} 0%, ${t.palette.primary.light} 100%)`,
            display: 'flex',
            gap: 3,
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>
              {userName ? `Welcome back, ${userName}` : 'Welcome to TourOptima'}
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: 640, opacity: 0.9 }}>
              Everything you need to run your tours from one place. Start with the step-by-step
              guides below — each one opens a click-through walkthrough in Scribe.
            </Typography>
          </Box>
          <Box
            sx={{
              textAlign: 'center',
              px: 2.5,
              py: 1.5,
              borderRadius: 1.5,
              bgcolor: 'rgba(255,255,255,.15)',
              border: '1px solid rgba(255,255,255,.25)',
            }}
          >
            <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>
              {TOTAL_GUIDES}+
            </Typography>
            <Typography variant="caption" sx={{ letterSpacing: '.08em', textTransform: 'uppercase' }}>
              How-to guides
            </Typography>
          </Box>
        </Paper>

        {/* Grouped guide cards */}
        {GUIDE_SECTIONS.map((section) => (
          <Box key={section.title} sx={{ mb: 3.75 }}>
            <SectionHeader title={section.title} subtitle={section.subtitle} />
            <Box
              sx={{
                display: 'grid',
                gap: 1.75,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              }}
            >
              {section.guides.map((guide) => (
                <GuideCard key={guide.title} guide={guide} />
              ))}
            </Box>
          </Box>
        ))}

        {/* Support + escalation */}
        <SectionHeader title="Help & Escalation" />
        <Box
          sx={{
            display: 'grid',
            gap: 1.75,
            gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
          }}
        >
          <Card variant="outlined" sx={{ borderRadius: 2, p: 2.25 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="subtitle1">Questions about the dashboard</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Reach the Operations team any time — we usually reply within one business day.
            </Typography>
            <Stack spacing={1.25} sx={{ mt: 1.75 }}>
              <SupportRow icon={MailOutlineIcon} label="Email">
                <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
              </SupportRow>
              <SupportRow icon={ChatBubbleOutlineIcon} label="In-app">
                Message the <strong>App Support</strong> channel in Chat
              </SupportRow>
            </Stack>
          </Card>

          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              p: 2.25,
              backgroundColor: '#fff4e5',
              borderColor: '#ffd8a8',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.dark' }} />
              <Typography variant="subtitle1" sx={{ color: 'warning.dark' }}>
                Emergencies
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#7a4402' }}>
              Check{' '}
              <Link href={STATUS_URL} target="_blank" rel="noopener noreferrer" sx={{ color: 'warning.dark', fontWeight: 500 }}>
                status.touroptima.com
              </Link>{' '}
              first. If the issue isn’t listed as a known disruption, call the 24/7 line.
            </Typography>
            <Stack spacing={1.25} sx={{ mt: 1.75 }}>
              <SupportRow icon={PhoneInTalkIcon} label="24/7 emergency line" tone="warning">
                <Link
                  href={`tel:${EMERGENCY_PHONE.replace(/[^\d+]/g, '')}`}
                  sx={{ color: 'warning.dark', fontWeight: 500 }}
                >
                  {EMERGENCY_PHONE}
                </Link>
              </SupportRow>
            </Stack>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
